/**
 * Checkpoint Client
 * 
 * The main entry point for the Checkpoint API client library.
 * Provides unified access to Authentication, Authorization, and Allocation.
 * 
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 */

import { HttpClient } from './http-client';
import { CheckpointAuth } from './auth';
import { CheckpointAuthz } from './authz';
import { CheckpointAlloc } from './alloc';
import { SecondaryUsers } from './secondary-users';
import {
  CheckpointConfig,
  UserProfile,
  User,
  CheckpointError,
} from './types';
import { DEFAULT_CHECKPOINT_APP, type CheckpointAppName } from './app-names';

/**
 * Main Checkpoint Client
 * 
 * @example
 * ```typescript
 * import { CheckpointClient } from '@appfactory/checkpoint-client';
 * 
 * // Initialize the client
 * const checkpoint = new CheckpointClient({
 *   baseUrl: 'https://api.yourapp.com',
 *   ssoDomain: 'https://members.appfactory.studio',
 *   onAuthStateChange: (authenticated, user) => {
 *     console.log('Auth state changed:', authenticated, user);
 *   }
 * });
 * 
 * // Authentication
 * await checkpoint.auth.login('user@example.com', 'password');
 * const user = await checkpoint.auth.getCurrentUser();
 * 
 * // Authorization
 * const canEdit = await checkpoint.authz.can('edit', 'article');
 * const isPremium = await checkpoint.authz.isFeatureEnabled('premium_features');
 * 
 * // Allocation
 * const { available, remaining } = await checkpoint.alloc.canUse('domains', 'email', 'send');
 * if (available) {
 *   await checkpoint.alloc.consume('domains', 'email', 'send');
 * }
 * ```
 */
export class CheckpointClient {
  /** Authentication module */
  public readonly auth: CheckpointAuth;

  /** Authorization module */
  public readonly authz: CheckpointAuthz;

  /** Allocation module */
  public readonly alloc: CheckpointAlloc;

  /** Secondary users module */
  public readonly secondaryUsers: SecondaryUsers;

  /** HTTP client for raw requests */
  private readonly http: HttpClient;

  /** Configuration */
  private readonly config: CheckpointConfig;

  /** expose ssoDomain for helpers that need it */
  public get ssoDomain(): string {
    return this.config.ssoDomain;
  }

  /** Application context for secondary-user auth (defaults to MEMBERS). */
  public get appName(): CheckpointAppName {
    return this.config.appName ?? DEFAULT_CHECKPOINT_APP;
  }

  /**
   * Create a new Checkpoint client instance
   * 
   * @param config Client configuration
   */
  constructor(config: CheckpointConfig) {
    if (!config.ssoDomain) {
      throw new Error('CheckpointConfig.ssoDomain is required');
    }
    this.config = {
      storage: 'localStorage',
      autoRefresh: true,
      refreshThreshold: 300, // 5 minutes
      timeout: 30000,
      ...config,
      appName: config.appName ?? DEFAULT_CHECKPOINT_APP,
    };

    this.http = new HttpClient(this.config);
    this.auth = new CheckpointAuth(this.http, this.config);
    this.authz = new CheckpointAuthz(this.http, this.config);
    this.alloc = new CheckpointAlloc(this.http, this.config);
    this.secondaryUsers = new SecondaryUsers(this.http, this.config);
  }

  /**
   * Initialize the client after authentication
   * 
   * This should be called after the user is authenticated to set up
   * Flagsmith and AccessControl for client-side feature/permission checks.
   * 
   * @example
   * ```typescript
   * await checkpoint.auth.login('user@example.com', 'password');
   * await checkpoint.initialize();
   * ```
   */
  async initialize(): Promise<void> {
    if (await this.isAuthenticated()) {
      await this.authz.initialize();
    }
  }

  /**
   * Persist an externally-obtained session into the HTTP client.
   *
   * Secondary-user tokens arrive via the SSO redirect URL rather than from a
   * direct API call on this domain, so they never pass through the HTTP
   * client's own token store. Without this, subsequent authenticated requests
   * (e.g. listing the app's secondary users) would be sent with no bearer
   * token. Call this after extracting the tokens from the callback so the
   * session is usable for API calls.
   *
   * @param tokens Access/refresh tokens obtained out-of-band (SSO callback)
   */
  async setSession(tokens: {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type?: string;
    refresh_expires_in?: number;
  }): Promise<void> {
    await this.http.setTokens({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token ?? '',
      token_type: tokens.token_type ?? 'Bearer',
      expires_in: tokens.expires_in,
      refresh_expires_in: tokens.refresh_expires_in ?? tokens.expires_in * 2,
    });
  }

  /**
   * Get the full user profile including auth, authz, and allocation data
   * 
   * @returns Complete user profile
   */
  async getProfile(): Promise<UserProfile> {
    return this.http.get<UserProfile>('/profile', true);
  }

  /**
   * Check if the user is authenticated
   * 
   * @returns True if authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return this.http.isAuthenticated();
  }

  /**
   * Get the current user
   * 
   * @returns Current user or null
   */
  async getCurrentUser(): Promise<User | null> {
    return this.auth.getCurrentUser();
  }

  /**
   * Require authentication - throws if not authenticated
   * 
   * @returns Current user
   * @throws CheckpointError if not authenticated
   */
  async requireAuth(): Promise<User> {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new CheckpointError('Authentication required', 'AUTH_REQUIRED', 401);
    }
    return user;
  }

  /**
   * Require a permission - throws if not allowed
   * 
   * @param action Action to check
   * @param resource Resource to check
   * @throws CheckpointError if not allowed
   */
  async requirePermission(action: string, resource: string): Promise<void> {
    await this.requireAuth();
    const allowed = await this.authz.can(action, resource);
    if (!allowed) {
      throw new CheckpointError('Permission denied', 'AUTH_PERMISSION_DENIED', 403);
    }
  }

  /**
   * Require a feature - throws if not enabled
   * 
   * @param featureKey Feature to check
   * @throws CheckpointError if feature is not enabled
   */
  async requireFeature(featureKey: string): Promise<void> {
    const enabled = await this.authz.isFeatureEnabled(featureKey);
    if (!enabled) {
      throw new CheckpointError('Feature not available', 'FEATURE_DISABLED', 403);
    }
  }

  /**
   * Require allocation - throws if limit exceeded
   * 
   * @param category Category
   * @param resource Resource
   * @param metric Metric
   * @param amount Amount to check
   * @throws CheckpointError if limit would be exceeded
   */
  async requireAllocation(
    category: string,
    resource: string,
    metric: string,
    amount = 1
  ): Promise<void> {
    await this.requireAuth();
    const { available } = await this.alloc.canUse(category, resource, metric, amount);
    if (!available) {
      throw new CheckpointError(
        `Allocation limit exceeded for ${category}/${resource}/${metric}`,
        'ALLOCATION_LIMIT_EXCEEDED',
        403
      );
    }
  }

  /**
   * Make a raw HTTP request to the API
   * 
   * @param method HTTP method
   * @param path API path
   * @param data Request body
   * @param requireAuth Require authentication
   * @returns Response data
   */
  async request<T>(
    method: string,
    path: string,
    data?: unknown,
    requireAuth = false
  ): Promise<T> {
    return this.http.request<T>(method, path, data, requireAuth);
  }

  /**
   * Check API health
   * 
   * @returns Health status
   */
  async healthCheck(): Promise<{ status: string; version: string; timestamp: number }> {
    return this.http.get('/health');
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this.authz.clearCache();
    this.alloc.clearCache();
  }

  /**
   * Clean up resources (call when done with the client)
   */
  destroy(): void {
    this.http.destroy();
  }
}

// Export all types
export * from './types';
export * from './app-names';

// Export individual modules
export { CheckpointAuth } from './auth';
export { CheckpointAuthz } from './authz';
export { CheckpointAlloc } from './alloc';
export { SecondaryUsers } from './secondary-users';
export { HttpClient } from './http-client';

// Export Allocation V2 module (server-side proxy, no Parse in browser)
export { CheckpointAllocV2, AllocV2Error } from './alloc_v2';
export type {
  AllocV2Config,
  AllocV2Scope,
  AllocV2Metric,
  AllocV2Resource,
  AllocV2Status,
  AllocV2CheckResult,
  AllocV2MutationResult,
  AllocV2MutationOptions,
  AllocV2Response,
} from './alloc_v2';

// Export SSO handler utilities
export { 
  handleSsoAuth, 
  hasSsoParams, 
  getUserTypeFromUrl, 
  buildSecondaryLoginUrl,
  type UserType,
  type SsoAuthResult,
} from './sso-handler';

// Export client handler
export {
  ClientHandler,
  type AuthState,
  type SsoCallbackParams,
  type AccountOption,
  type PageLoadResult,
  type ClientUserType,
} from './client-handler';

// Default export
export default CheckpointClient;
